export type AccessToOptions = {
  /**
   * If true, include the tenantId from the record in the accessTo object.
   * If a string is provided, use that string as the key to retrieve the tenantId from the record.
   * If false, do not include tenantId in the accessTo object.
   * @default true
   */
  tenant?: boolean | string;
  /**
   * If true, include the assetId from the record in the accessTo object.
   * If a string is provided, use that string as the key to retrieve the assetId from the record.
   * If false, do not include assetId in the accessTo object.
   * @default true
   */
  asset?: boolean | string;
};

const getValue = (record: Record<string, unknown>, key: string | boolean, defaultKey: string) => {
  if (typeof key === 'string') {
    return record[key];
  }

  if (key) {
    return record[defaultKey];
  }

  return undefined;
};

/**
 * Build accessTo object for a given record and options.
 *
 * @param record The record containing resource identifiers.
 * @param options Options specifying which resources to include.
 * @returns An object with resource and ids if access is granted, otherwise undefined.
 */
export const getAccessToForRecord = (
  record: Record<string, unknown>,
  options?: AccessToOptions,
) => {
  const { asset: includeAsset = true, tenant: includeTenant = true } = options ?? {};

  const assetId = getValue(record, includeAsset, 'assetId');
  const tenantId = getValue(record, includeTenant, 'tenantId');

  if (includeAsset && assetId && typeof assetId === 'string') {
    return { resource: 'assets', ids: [assetId] };
  }

  if (includeTenant && tenantId && typeof tenantId === 'string') {
    return { resource: 'tenants', ids: [tenantId] };
  }

  return undefined;
};
